import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  Input,
  LoadingSkeleton,
  Modal,
  Select,
} from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../../shared/utils/format';
import { useCustomers } from '../../master-data/useMasterData';
import { useProductLookup } from '../../../shared/lookups/useParties';
import {
  useContracts,
  useCreateContract,
  type ContractPeriodicity,
  type RecurringContract,
} from '../../crm/useContracts';

/* Repositório global de contratos.
 * GET /contracts (lista paginada) e POST /contracts (novo).
 * O contrato não tem categoria nem fornecedor: a coluna usa a periodicidade
 * e o nome do cliente resolvido em GET /customers.
 */

const PERIODICITY_LABEL: Record<ContractPeriodicity, string> = {
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quinzenal',
  MONTHLY: 'Mensal',
  BIMONTHLY: 'Bimestral',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
};

const STATUS_LABEL: Record<RecurringContract['status'], string> = {
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  CANCELLED: 'Cancelado',
};

const STATUS_VARIANT: Record<RecurringContract['status'], 'success' | 'warning' | 'error'> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  CANCELLED: 'error',
};

function contractLabel(contract: RecurringContract): string {
  return contract.items[0]?.description || `Contrato ${contract.id.slice(0, 8)}`;
}

function withinDays(iso: string | null, days: number): boolean {
  if (!iso) return false;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return false;
  const now = Date.now();
  return end >= now && end <= now + days * 24 * 60 * 60 * 1000;
}

function downloadCsv(rows: RecurringContract[], nameOf: (id: string) => string): void {
  const header = ['id', 'cliente', 'descricao', 'periodicidade', 'valor', 'proximoFaturamento', 'fim', 'status'];
  const lines = rows.map((c) =>
    [
      c.id,
      nameOf(c.clientId),
      contractLabel(c),
      PERIODICITY_LABEL[c.periodicity] ?? c.periodicity,
      String(c.value),
      c.nextBillingDate,
      c.endDate ?? '',
      c.status,
    ]
      .map((cell) => `"${cell.replaceAll('"', '""')}"`)
      .join(','),
  );
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'contratos.csv';
  link.click();
  URL.revokeObjectURL(url);
}

export function ContractRepositoryPage(): JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [formError, setFormError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productId, setProductId] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitValue, setUnitValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [periodicity, setPeriodicity] = useState<ContractPeriodicity>('MONTHLY');

  const list = useContracts({ status: status || undefined, page: 1, limit: 100 });
  const customers = useCustomers();
  const createContract = useCreateContract();
  const productLookup = useProductLookup(productSearch);
  const clientMatches = (customers.data ?? [])
    .filter((customer) => {
      const term = clientSearch.trim().toLowerCase();
      if (term.length < 2 || clientId) return false;
      return customer.name.toLowerCase().includes(term)
        || (customer.documentNumber ?? '').toLowerCase().includes(term);
    })
    .slice(0, 8);

  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const customer of customers.data ?? []) {
      map.set(customer.id, customer.name);
      if (customer.partyId) map.set(customer.partyId, customer.name);
    }
    return (id: string) => map.get(id) ?? id;
  }, [customers.data]);

  const rows = useMemo(() => {
    const data = list.data?.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter((contract) => {
      const haystack = [
        contract.id,
        contract.clientId,
        nameOf(contract.clientId),
        contractLabel(contract),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [list.data?.data, nameOf, search]);

  const activeValue = useMemo(
    () => rows.filter((c) => c.status === 'ACTIVE').reduce((sum, c) => sum + c.value, 0),
    [rows],
  );
  const expiring = useMemo(
    () => rows.filter((c) => c.status === 'ACTIVE' && withinDays(c.endDate, 30)).length,
    [rows],
  );

  const openNew = (): void => {
    setFormError('');
    setClientSearch('');
    setClientId('');
    setClientName('');
    setProductSearch('');
    setProductId('');
    setDescription('');
    setQuantity('1');
    setUnitValue('');
    setStartDate('');
    setPeriodicity('MONTHLY');
    setShowNew(true);
  };

  const submitNew = (): void => {
    const qty = Number(quantity);
    const unit = Number(unitValue);
    if (!clientId) {
      setFormError('Selecione o cliente.');
      return;
    }
    if (!productId || !description.trim()) {
      setFormError('Selecione o produto do item.');
      return;
    }
    if (!startDate) {
      setFormError('Informe a data de início.');
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(unit) || unit < 0) {
      setFormError('Quantidade e valor unitário precisam ser números válidos.');
      return;
    }
    createContract.mutate(
      {
        clientId,
        startDate,
        periodicity,
        value: qty * unit,
        items: [{ productId, description: description.trim(), quantity: qty, unitValue: unit }],
      },
      {
        onSuccess: (created) => {
          setShowNew(false);
          void navigate({ to: '/contracts/$id', params: { id: created.id } });
        },
        onError: (error: unknown) => {
          setFormError(error instanceof Error ? error.message : 'Não foi possível criar o contrato.');
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-lg p-xl">
      <div className="flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">
            Repositório de Contratos
          </h1>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Contratos recorrentes do tenant, com valor, vigência e situação.
          </p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button variant="outline" onClick={() => downloadCsv(rows, nameOf)} disabled={rows.length === 0}>
            Exportar lista
          </Button>
          <Button variant="primary" onClick={openNew}>
            Novo contrato
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-md md:grid-cols-2">
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Valor dos contratos ativos na lista
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {formatCurrency(activeValue)}
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Ativos com fim em até 30 dias
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">{expiring}</p>
        </Card>
      </div>

      <div className="flex flex-wrap gap-sm">
        <Input
          aria-label="Buscar contratos"
          placeholder="Buscar por cliente, descrição ou id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select aria-label="Situação" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todas as situações</option>
          <option value="ACTIVE">Ativo</option>
          <option value="SUSPENDED">Suspenso</option>
          <option value="CANCELLED">Cancelado</option>
        </Select>
      </div>

      {list.isLoading ? (
        <Card>
          <LoadingSkeleton count={4} height="40px" />
        </Card>
      ) : null}

      {list.error ? (
        <Card>
          <p className="m-0 font-body-md text-body-md text-error">{(list.error as Error).message}</p>
        </Card>
      ) : null}

      {!list.isLoading && !list.error && rows.length === 0 ? (
        <Card>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Nenhum contrato encontrado para este filtro.
          </p>
        </Card>
      ) : null}

      {!list.isLoading && !list.error && rows.length > 0 ? (
        <Card padding="0">
          <div className="border-b border-outline-variant p-md">
            <h2 className="m-0 font-headline-sm text-headline-sm text-on-surface">
              Diretório ({list.data?.total ?? rows.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-outline-variant">
                  <th className="px-md py-sm font-label-md text-label-md text-on-surface-variant">Contrato / cliente</th>
                  <th className="px-md py-sm font-label-md text-label-md text-on-surface-variant">Periodicidade</th>
                  <th className="px-md py-sm text-right font-label-md text-label-md text-on-surface-variant">Valor</th>
                  <th className="px-md py-sm font-label-md text-label-md text-on-surface-variant">Próximo faturamento</th>
                  <th className="px-md py-sm font-label-md text-label-md text-on-surface-variant">Situação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((contract) => (
                  <tr
                    key={contract.id}
                    className="cursor-pointer border-b border-outline-variant last:border-b-0 hover:bg-surface-container-low"
                    onClick={() => void navigate({ to: '/contracts/$id', params: { id: contract.id } })}
                  >
                    <td className="px-md py-sm">
                      <div className="font-body-md text-body-md font-semibold text-on-surface">
                        {contractLabel(contract)}
                      </div>
                      <div className="font-body-sm text-body-sm text-on-surface-variant">
                        {nameOf(contract.clientId)}
                      </div>
                    </td>
                    <td className="px-md py-sm font-body-sm text-body-sm text-on-surface-variant">
                      {PERIODICITY_LABEL[contract.periodicity] ?? contract.periodicity}
                    </td>
                    <td className="px-md py-sm text-right font-body-md text-body-md text-on-surface">
                      {formatCurrency(contract.value)}
                    </td>
                    <td className="px-md py-sm font-body-sm text-body-sm text-on-surface-variant">
                      {formatDate(contract.nextBillingDate)}
                      {contract.endDate ? ` · fim ${formatDate(contract.endDate)}` : ''}
                    </td>
                    <td className="px-md py-sm">
                      <Badge variant={STATUS_VARIANT[contract.status]}>{STATUS_LABEL[contract.status]}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <Modal
        isOpen={showNew}
        onClose={() => setShowNew(false)}
        title="Novo contrato"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button variant="primary" onClick={submitNew} loading={createContract.isPending}>
              Criar contrato
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-md">
          {formError ? <p className="m-0 font-body-sm text-body-sm text-error">{formError}</p> : null}
          <Input
            label="Cliente *"
            value={clientSearch}
            placeholder="Nome ou documento"
            onChange={(e) => {
              setClientSearch(e.target.value);
              setClientId('');
              setClientName('');
            }}
          />
          {clientName ? (
            <p className="m-0 font-body-sm text-body-sm text-on-surface">Selecionado: {clientName}</p>
          ) : null}
          {(clientMatches).map((customer) => (
            <Button
              key={customer.id}
              size="sm"
              variant="outline"
              onClick={() => {
                setClientId(customer.id);
                setClientName(customer.name);
                setClientSearch(customer.name);
              }}
            >
              {customer.name}
            </Button>
          ))}
          <Input label="Início *" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Select
            aria-label="Periodicidade"
            value={periodicity}
            onChange={(e) => setPeriodicity(e.target.value as ContractPeriodicity)}
          >
            {Object.entries(PERIODICITY_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
          <Input
            label="Produto *"
            value={productSearch}
            placeholder="Nome ou SKU"
            onChange={(e) => {
              setProductSearch(e.target.value);
              setProductId('');
            }}
          />
          {(productLookup.data ?? []).map((product) => (
            <Button
              key={product.id}
              size="sm"
              variant="outline"
              onClick={() => {
                setProductId(product.id);
                setDescription(product.name);
                setProductSearch(product.name);
              }}
            >
              {product.sku} · {product.name}
            </Button>
          ))}
          <Input label="Descrição do item *" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="flex gap-sm">
            <Input label="Quantidade *" type="number" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            <Input label="Valor unitário *" type="number" min="0" step="0.01" value={unitValue} onChange={(e) => setUnitValue(e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
